import { PrimitiveIcon, UIIcon } from "@gluestack-ui/core/icon/creator";
import { createInput } from "@gluestack-ui/core/input/creator";
import { VariantProps, tva, useStyleContext, withStyleContext } from "@gluestack-ui/utils";
import { cssInterop } from "nativewind";
import React from "react";
import { Pressable, TextInput, View } from "react-native";

const SCOPE = "INPUT";

const UIInput = createInput({
	Root: withStyleContext(View, SCOPE),
	Icon: UIIcon,
	Slot: Pressable,
	Input: TextInput,
});

cssInterop(PrimitiveIcon, {
	className: {
		target: "style",
		nativeStyleToProp: {
			height: true,
			width: true,
			fill: true,
			color: "classNameColor",
			stroke: true,
		} as any,
	},
});

const inputStyle = tva({
	base: "border-secondary-100 flex-row overflow-hidden content-center data-[hover=true]:border-secondary-200 data-[focus=true]:border-secondary-500 data-[disabled=true]:opacity-40 items-center gap-2",
	variants: {
		size: {
			xl: "h-16 px-5",
			lg: "h-14 px-4",
			md: "h-12 px-4",
			sm: "h-10 px-3",
		},
		variant: {
			underlined:
				"rounded-none border-b data-[focus=true]:border-b-2 data-[invalid=true]:border-b-2 data-[invalid=true]:border-error-500",
			outline: "rounded-lg border data-[invalid=true]:border-error-500",
			rounded: "rounded-full border data-[invalid=true]:border-error-500",
		},
	},
	compoundVariants: [
		{
			variant: "underlined",
			size: "sm",
			class: "px-0",
		},
		{
			variant: "underlined",
			size: "md",
			class: "px-0",
		},
		{
			variant: "underlined",
			size: "lg",
			class: "px-0",
		},
		{
			variant: "underlined",
			size: "xl",
			class: "px-0",
		},
	],
});

const inputIconStyle = tva({
	base: "justify-center items-center text-secondary-100 fill-none",
	variants: {
		isInvalid: {
			true: "text-error-500",
		},
	},
	parentVariants: {
		size: {
			sm: "h-4 w-4",
			md: "h-[18px] w-[18px]",
			lg: "h-6 w-6",
			xl: "h-6 w-6",
		},
	},
});

const inputSlotStyle = tva({
	base: "justify-center items-center web:disabled:cursor-not-allowed",
});

const inputFieldStyle = tva({
	base: "flex-1 text-neutral-950 py-0 placeholder:text-secondary-50 h-full ios:leading-[0px] web:cursor-text web:data-[disabled=true]:cursor-not-allowed",
	parentVariants: {
		variant: {
			underlined: "web:outline-0 web:outline-none",
			outline: "web:outline-0 web:outline-none",
			rounded: "web:outline-0 web:outline-none",
		},
		size: {
			sm: "text-xs",
			md: "text-sm",
			lg: "text-base",
			xl: "text-lg",
		},
	},
});

export type IInputProps = React.ComponentProps<typeof UIInput> &
	VariantProps<typeof inputStyle> & { className?: string };
const Input = React.forwardRef<React.ComponentRef<typeof UIInput>, IInputProps>(function Input(
	{ className, variant = "outline", size = "md", ...props },
	ref,
) {
	return (
		<UIInput
			ref={ref}
			{...props}
			className={inputStyle({ variant, size, class: className })}
			context={{ variant, size }}
		/>
	);
});

type IInputIconProps = React.ComponentProps<typeof UIInput.Icon> &
	VariantProps<typeof inputIconStyle> & {
		className?: string;
		height?: number;
		width?: number;
		isInvalid?: boolean;
	};

const InputIcon = React.forwardRef<React.ComponentRef<typeof UIInput.Icon>, IInputIconProps>(function InputIcon(
	{ className, size, isInvalid, ...props },
	ref,
) {
	const { size: parentSize } = useStyleContext(SCOPE);

	if (typeof size === "number") {
		return <UIInput.Icon ref={ref} {...props} className={inputIconStyle({ class: className })} size={size} />;
	}

	if ((props.height !== undefined || props.width !== undefined) && size === undefined) {
		return <UIInput.Icon ref={ref} {...props} className={inputIconStyle({ class: className })} />;
	}
	return (
		<UIInput.Icon
			ref={ref}
			{...props}
			className={inputIconStyle({
				isInvalid,
				parentVariants: {
					size: parentSize,
				},
				class: className,
			})}
		/>
	);
});

type IInputSlotProps = React.ComponentProps<typeof UIInput.Slot> &
	VariantProps<typeof inputSlotStyle> & { className?: string };

const InputSlot = React.forwardRef<React.ComponentRef<typeof UIInput.Slot>, IInputSlotProps>(function InputSlot(
	{ className, ...props },
	ref,
) {
	return (
		<UIInput.Slot
			ref={ref}
			{...props}
			className={inputSlotStyle({
				class: className,
			})}
		/>
	);
});

type IInputFieldProps = React.ComponentProps<typeof UIInput.Input> &
	VariantProps<typeof inputFieldStyle> & { className?: string };

const InputField = React.forwardRef<React.ComponentRef<typeof UIInput.Input>, IInputFieldProps>(function InputField(
	{ className, ...props },
	ref,
) {
	const { variant: parentVariant, size: parentSize } = useStyleContext(SCOPE);

	return (
		<UIInput.Input
			ref={ref}
			{...props}
			className={inputFieldStyle({
				parentVariants: {
					variant: parentVariant,
					size: parentSize,
				},
				class: className,
			})}
		/>
	);
});

Input.displayName = "Input";
InputIcon.displayName = "InputIcon";
InputSlot.displayName = "InputSlot";
InputField.displayName = "InputField";

export { Input, InputField, InputIcon, InputSlot };
